/**
 * Toronto timezone utilities for consistent time handling
 */

/**
 * Get Toronto time from any date
 * @param {Date} date - Date to convert
 * @returns {Date} - Date adjusted to Toronto timezone
 */
export function getTorontoTime(date) {
  // Create a new date in Toronto timezone
  const torontoTime = new Date(date.toLocaleString("en-US", {timeZone: "America/Toronto"}))
  return torontoTime
}

/**
 * Get Toronto hour from any date (0-23 format)
 * @param {Date} date - Date to get hour from
 * @returns {number} - Hour in Toronto timezone
 */
export function getTorontoHour(date) {
  return parseInt(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto',
    hour: 'numeric',
    hour12: false
  }).format(date))
}

/**
 * Format time for Toronto timezone display
 * @param {Date} date - Date to format
 * @returns {string} - Formatted time string
 */
export function formatTorontoTime(date) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date)
}

/**
 * Get current timezone abbreviation for Toronto
 * @param {Date} date - Date to check timezone for
 * @returns {string} - EDT or EST
 */
export function getTorontoTimezone(date) {
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto',
    timeZoneName: 'short'
  }).format(date)
  
  // Extract timezone abbreviation
  const match = formatted.match(/\b[A-Z]{3,4}\b/)
  return match ? match[0] : 'EDT'
}

/**
 * Create conference atmosphere data based on time
 * @param {Date} date - Conference time
 * @returns {Object} - Atmosphere data
 */
export function getConferenceAtmosphere(date) {
  const hour = getTorontoHour(date)
  
  if (hour < 9) {
    return {
      energy: 'low',
      description: 'Pre-conference setup',
      emoji: '🌅',
      color: 'from-blue-500/20 to-purple-500/20',
      density: 0
    }
  } else if (hour >= 9 && hour < 11) {
    return {
      energy: 'building',
      description: 'Morning energy building',
      emoji: '☀️',
      color: 'from-green-500/20 to-blue-500/20',
      density: 60
    }
  } else if (hour >= 11 && hour < 13) {
    return {
      energy: 'peak',
      description: 'Peak conference energy',
      emoji: '🔥',
      color: 'from-orange-500/20 to-red-500/20',
      density: 100
    }
  } else if (hour >= 13 && hour < 15) {
    return {
      energy: 'lunch',
      description: 'Lunch networking buzz',
      emoji: '🤝',
      color: 'from-yellow-500/20 to-orange-500/20',
      density: 80
    }
  } else if (hour >= 15 && hour < 17) {
    return {
      energy: 'focused',
      description: 'Afternoon deep dives',
      emoji: '⚡',
      color: 'from-purple-500/20 to-blue-500/20',
      density: 75
    }
  } else {
    return {
      energy: 'winding',
      description: 'Day winding down',
      emoji: '🌆',
      color: 'from-indigo-500/20 to-purple-500/20',
      density: 70
    }
  }
}

/**
 * Get peak moment indicators for timeline
 * @returns {Array} - Array of peak moments
 */
export function getPeakMoments() {
  return [
    {
      time: '09:00',
      label: 'Conference Opens',
      icon: '🎉',
      type: 'start'
    },
    {
      time: '10:30',
      label: 'Keynote Peak',
      icon: '🎯',
      type: 'keynote'
    },
    {
      time: '11:00',
      label: 'Busiest Period',
      icon: '🔥',
      type: 'peak'
    },
    {
      time: '13:00',
      label: 'Lunch Networking',
      icon: '🤝',
      type: 'social'
    },
    {
      time: '15:30',
      label: 'Workshop Rush',
      icon: '⚡',
      type: 'workshop'
    },
    {
      time: '17:00',
      label: 'Day Concludes',
      icon: '🌅',
      type: 'end'
    }
  ]
} 